import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../store';
import { retryAuthValidation, resetRetryCount, clearError, setValidationState } from '../../store/authSlice';
import NetworkDetection from '../../utils/networkDetection';

interface AuthStatusIndicatorProps {
  className?: string;
  showDetails?: boolean;
}

/** Auto-dismiss window for informational states (ms). Actionable states stay. */
const AUTO_DISMISS_MS = 12000;

type Accent = 'cyan' | 'amber' | 'rose';

const ACCENT_STYLES: Record<Accent, { chip: string; bar: string }> = {
  cyan: {
    chip: 'bg-cyan-400/15 text-cyan-200 ring-cyan-300/30',
    bar: 'from-cyan-400/70 via-sky-400/40 to-transparent',
  },
  amber: {
    chip: 'bg-amber-400/15 text-amber-200 ring-amber-300/30',
    bar: 'from-amber-400/70 via-amber-400/30 to-transparent',
  },
  rose: {
    chip: 'bg-rose-400/15 text-rose-200 ring-rose-300/30',
    bar: 'from-rose-400/70 via-rose-400/30 to-transparent',
  },
};

/**
 * Floating auth/session status toast shown inside the app.
 *
 * Styled to match the platform's dark glass design language (translucent slate
 * card, blurred backdrop, hairline border, accent chip) instead of the old
 * light-mode alert box, which looked bolted-on over the dark screens.
 *
 * It deliberately renders nothing on the login/register pages: those pages show
 * the same information inline via <SessionEndedNotice>, so a floating copy would
 * only overlap the branding and offer a redundant "Log in" button.
 */
export const AuthStatusIndicator: React.FC<AuthStatusIndicatorProps> = ({
  className = '',
  showDetails = false
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { validationState, loading, error, retryCount, networkError, lastValidation, isAuthenticated, user } = useAppSelector(state => state.auth);
  const [isExpanded, setIsExpanded] = useState(false);

  const isAuthPage = location.pathname.includes('/login') || location.pathname.includes('/register');

  const handleDismiss = useCallback(() => {
    // Clear everything the render condition checks, otherwise the banner
    // reappears immediately (resetRetryCount alone leaves `error` set).
    dispatch(resetRetryCount());
    dispatch(clearError());
    if (validationState === 'invalid') {
      dispatch(setValidationState('idle'));
    }
  }, [dispatch, validationState]);

  // Informational states fade themselves out; 'invalid' needs a user decision,
  // so it waits for the dismiss button or a successful sign-in.
  useEffect(() => {
    if (!error || validationState === 'invalid' || validationState === 'validating') return;
    const timer = setTimeout(handleDismiss, AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [error, validationState, handleDismiss]);

  // Don't show indicator if everything is working normally or if there's no actual error
  if (validationState === 'valid' || validationState === 'idle' || !error) {
    return null;
  }

  // The login/register pages surface this inline instead (see SessionEndedNotice).
  if (isAuthPage) {
    return null;
  }

  // Don't show if user is authenticated and it's just background validation issues
  // Only show if it's actually affecting the user's ability to use the app
  if (validationState === 'failed' && isAuthenticated && user) {
    // Only show if it's been failing for a long time (5+ retries) or it's a network issue
    if (!networkError && retryCount < 5) {
      return null;
    }
  }

  const handleRetry = () => {
    dispatch(retryAuthValidation());
  };

  const handleGoToLogin = () => {
    handleDismiss();
    navigate('/onecare/login');
  };

  const getAccent = (): Accent => {
    switch (validationState) {
      case 'validating':
        return 'cyan';
      case 'failed':
        return 'amber';
      case 'invalid':
        return 'rose';
      default:
        return 'cyan';
    }
  };

  const getTitle = () => {
    switch (validationState) {
      case 'validating':
        return 'Checking your connection';
      case 'failed':
        if (networkError) {
          return NetworkDetection.isOnline() ? 'Connection unstable' : "You're offline";
        }
        return 'Background sync delayed';
      case 'invalid':
        return 'Your session ended';
      default:
        return 'Authentication status';
    }
  };

  const getDescription = () => {
    switch (validationState) {
      case 'validating':
        return 'Confirming your session with the server.';
      case 'failed':
        if (networkError) {
          return NetworkDetection.isOnline()
            ? 'Some updates may be delayed. We keep retrying in the background.'
            : 'We will reconnect automatically once your connection returns.';
        }
        return 'Your work is safe — some updates may take a moment to arrive.';
      case 'invalid':
        return 'You were signed out to keep your account secure. Sign in again to continue.';
      default:
        return '';
    }
  };

  const getIcon = () => {
    switch (validationState) {
      case 'validating':
        return (
          <svg className="animate-spin h-3.5 w-3.5" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        );
      case 'failed':
        return (
          <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        );
      case 'invalid':
        return (
          <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        );
      default:
        return null;
    }
  };

  const accent = ACCENT_STYLES[getAccent()];
  const showActions = validationState === 'invalid' || (validationState === 'failed' && retryCount < 3) || showDetails;

  return (
    <div
      role="status"
      aria-live="polite"
      className={`auth-toast-in overflow-hidden rounded-2xl border border-white/10 bg-slate-900/90 shadow-2xl shadow-black/40 backdrop-blur-xl ${className}`}
    >
      {/* Accent hairline — carries the state colour without shouting */}
      <div className={`h-px w-full bg-gradient-to-r ${accent.bar}`} />

      <div className="flex items-start gap-3 p-3.5">
        <span
          className={`mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full ring-1 ${accent.chip}`}
          aria-hidden="true"
        >
          {getIcon()}
        </span>

        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold leading-snug text-white break-words">{getTitle()}</p>
          <p className="mt-0.5 text-xs leading-snug text-slate-400 break-words">{getDescription()}</p>

          {showActions && (
            <div className="mt-2.5 flex flex-wrap items-center gap-2">
              {validationState === 'invalid' && (
                <button
                  onClick={handleGoToLogin}
                  className="rounded-lg bg-cyan-400/15 px-2.5 py-1 text-xs font-semibold text-cyan-100 ring-1 ring-cyan-300/30 transition-colors hover:bg-cyan-400/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/60"
                >
                  Sign in again
                </button>
              )}

              {validationState === 'failed' && retryCount < 3 && (
                <button
                  onClick={handleRetry}
                  disabled={loading}
                  className="rounded-lg bg-white/10 px-2.5 py-1 text-xs font-medium text-slate-200 ring-1 ring-white/15 transition-colors hover:bg-white/15 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                >
                  {loading ? 'Retrying...' : 'Retry now'}
                </button>
              )}

              {showDetails && (
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="rounded-lg bg-white/5 px-2.5 py-1 text-xs font-medium text-slate-300 ring-1 ring-white/10 transition-colors hover:bg-white/10"
                >
                  {isExpanded ? 'Hide details' : 'Details'}
                </button>
              )}
            </div>
          )}
        </div>

        <button
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="-m-1 flex-shrink-0 rounded-md p-1 text-slate-500 transition-colors hover:bg-white/10 hover:text-slate-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
        >
          <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {isExpanded && showDetails && (
        <div className="border-t border-white/10 bg-black/20 px-3.5 py-3 text-xs space-y-1 text-slate-400">
          <div>Status: <span className="font-mono text-slate-200">{validationState}</span></div>
          <div>Retry Count: <span className="font-mono text-slate-200">{retryCount}/3</span></div>
          <div>Network Error: <span className="font-mono text-slate-200">{networkError ? 'Yes' : 'No'}</span></div>
          <div>Online: <span className="font-mono text-slate-200">{NetworkDetection.isOnline() ? 'Yes' : 'No'}</span></div>
          {lastValidation && (
            <div>Last Validation: <span className="font-mono text-slate-200">{new Date(lastValidation).toLocaleTimeString()}</span></div>
          )}
          {error && (
            <div className="break-words">Error: <span className="font-mono text-slate-200">{error}</span></div>
          )}
        </div>
      )}
    </div>
  );
};

export default AuthStatusIndicator;
